<script setup>
// advup — dropdown de opção padrão (estilo do picker de Tags): caixa arredondada +
// painel com sombra + bolinha colorida opcional + hover + check no selecionado.
// Substitui <select> nativo feio. Uso: <AdvupSelect v-model="x" :options="[{value,label,color?}]" />
import { ref, computed, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number, null], default: '' },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Selecione' },
  // O nome do seletor para o leitor de tela. O rótulo visível costuma morar
  // FORA do componente e não se liga ao botão — sem isto a pessoa ouvia só o
  // valor ("Mais recentes, botão") sem saber do que ele é valor.
  rotulo: { type: String, default: '' },
  minWidth: { type: String, default: '140px' },
  disabled: { type: Boolean, default: false },
  // Pedido do Victor (2026-07-23): dropdown perto do rodapé da página
  // (ex: "Linhas por página" no fim de uma lista) fica cortado pelo
  // scroll do container quando abre pra baixo. Prop opt-in em vez de
  // detecção automática de viewport pra não arriscar regressão nos
  // ~10 outros usos deste componente.
  openUp: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'change']);

const open = ref(false);
const hasDots = computed(() => props.options.some(o => o.color));
// Agrupa preservando a ORDEM em que os grupos aparecem na lista.
const gruposDeOpcoes = computed(() =>
  props.options.reduce((grupos, o) => {
    const nome = o.group || '';
    const atual = grupos.find(g => g.nome === nome);
    if (atual) atual.itens.push(o);
    else grupos.push({ nome, itens: [o] });
    return grupos;
  }, [])
);
const current = computed(() =>
  props.options.find(o => o.value === props.modelValue)
);

// 🔴 A LISTA VIVE FORA DA ÁRVORE DO CAMPO, PRESA À TELA.
//
// Ela era `absolute` dentro do próprio componente, e qualquer ancestral com
// `overflow` a cortava. Relatado pelo Dr. Henrique em 08/09: no painel
// "Agendar" da publicação o dropdown de Responsável "abre atrás" — o corpo do
// modal é `overflow-y-auto` (PublicationScheduleModal.vue:250) e ficava só uma
// faixa da lista visível.
//
// A prop `openUp` (julho/2026) foi o remendo anterior do MESMO defeito, para o
// dropdown do rodapé de listas. Ela continua funcionando, mas agora é só
// preferência de direção: quem resolve o corte é o `Teleport` para o body.
const gatilho = ref(null);
const menu = ref(null);
const estiloMenu = ref({});

// Medir na hora de abrir, e não no mount: dentro de container que rola, a
// posição do campo muda a cada rolagem.
const posicionar = () => {
  const el = gatilho.value;
  if (!el) return;

  const r = el.getBoundingClientRect();
  const espacoAbaixo = window.innerHeight - r.bottom;
  // 288px = max-h-72 da lista. Sem espaço embaixo, abre para cima sozinha —
  // é o que a prop `openUp` fazia à mão.
  const paraCima = props.openUp || espacoAbaixo < 288;

  // NO CELULAR A LISTA ESTOURAVA A TELA.
  //
  // `left` era copiado do campo sem perguntar se a lista cabia à direita. Num
  // aparelho de 402px, o filtro de status abria em x=189 com 229px de largura
  // e terminava em 418: a opção "Finalizada (evento de negócio)" ficava
  // cortada pela borda. A lista já está renderizada aqui (o `posicionar()` é
  // chamado depois do `nextTick`), então dá para medir a largura de verdade em
  // vez de estimar.
  //
  // Só abaixo de 640px: no desktop a conta segue exatamente a de antes. O
  // mesmo estouro pode acontecer lá se o campo estiver colado na borda
  // direita, mas isso é outra conversa e outro escopo.
  const MARGEM = 8;
  let esquerda = r.left;
  if (window.innerWidth < 640) {
    const larguraLista = Math.max(
      menu.value?.getBoundingClientRect().width || 0,
      r.width
    );
    const limite = window.innerWidth - larguraLista - MARGEM;
    esquerda = Math.max(MARGEM, Math.min(r.left, limite));
  }

  estiloMenu.value = {
    position: 'fixed',
    left: `${esquerda}px`,
    minWidth: `${r.width}px`,
    ...(paraCima
      ? { bottom: `${window.innerHeight - r.top + 4}px` }
      : { top: `${r.bottom + 4}px` }),
  };
};

// Rolar A PÁGINA com a lista aberta deixaria ela flutuando longe do campo,
// porque a posição é fixa na tela. Fechar é mais honesto que perseguir o campo.
const fechar = () => {
  open.value = false;
};

// 🔴 MAS ROLAR DENTRO DA PRÓPRIA LISTA NÃO É ROLAR A PÁGINA.
//
// O listener de scroll é registrado com `capture: true`, então ele enxergava
// também o scroll de dentro da lista — que tem altura máxima e rola por conta
// própria (`max-h-72 overflow-y-auto`). Quem tentava descer para ler as
// opções via a lista fechar na cara. Relatado pelo Victor em 09/09/2026 no
// modal "Agendar a partir desta publicação", onde tanto o campo Tipo quanto o
// Responsável têm mais itens do que cabem na altura da lista.
const aoRolar = evento => {
  if (evento.target instanceof Node && menu.value?.contains(evento.target)) {
    return;
  }

  fechar();
};

const alternar = async () => {
  if (open.value) {
    fechar();
    return;
  }

  open.value = true;
  await nextTick();
  posicionar();
  window.addEventListener('scroll', aoRolar, true);
  window.addEventListener('resize', fechar);
};

onBeforeUnmount(() => {
  window.removeEventListener('scroll', aoRolar, true);
  window.removeEventListener('resize', fechar);
});

const pick = o => {
  fechar();
  emit('update:modelValue', o.value);
  emit('change', o.value);
};
</script>

<template>
  <div class="relative">
    <button
      ref="gatilho"
      type="button"
      class="adv-sel-trigger"
      :aria-label="
        rotulo ? `${rotulo}: ${current?.label || placeholder}` : undefined
      "
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      :style="{ minWidth }"
      :disabled="disabled"
      @click="alternar"
    >
      <span class="flex items-center gap-2 min-w-0">
        <span
          v-if="current?.color"
          class="size-2 rounded-full flex-shrink-0"
          :style="{ background: current.color }"
        />
        <span
          class="truncate"
          :class="current ? 'text-n-slate-12 font-medium' : 'text-n-slate-10'"
        >
          {{ current?.label || placeholder }}
        </span>
      </span>
      <span class="i-ph-caret-down size-4 text-n-slate-10 flex-shrink-0" />
    </button>

    <!-- O `v-if` mora dentro do Teleport (24/10/2026): por fora, a lista sumia seca; aqui ela
         sai por onde entrou (`.advup-pop`, _advup-tela.scss). -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="fechar" />
      <Transition name="advup-pop">
        <div
          v-if="open"
          ref="menu"
          class="adv-sel-menu advup-pop"
          :style="estiloMenu"
        >
          <template
            v-for="grupo in gruposDeOpcoes"
            :key="grupo.nome || 'sem-grupo'"
          >
            <!-- O título de grupo existe porque `<optgroup>` existia: a lista de
               campos de uma condição tem quatro famílias (contato, conversa,
               cartão, etiqueta), e sem a divisão viram 20 linhas seguidas em
               que nada diz de onde cada campo vem. Sem `group` nas opções,
               nada muda: um grupo só, sem título. -->
            <p v-if="grupo.nome" class="adv-sel-grupo">{{ grupo.nome }}</p>
            <button
              v-for="o in grupo.itens"
              :key="o.value"
              type="button"
              class="adv-sel-item"
              @click="pick(o)"
            >
              <span
                v-if="o.color"
                class="size-2 rounded-full flex-shrink-0"
                :style="{ background: o.color }"
              />
              <span v-else-if="hasDots" class="size-2 flex-shrink-0" />
              <span class="flex-1 text-left truncate">{{ o.label }}</span>
              <span
                v-if="modelValue === o.value"
                class="i-ph-check size-3.5 text-n-blue-11 flex-shrink-0"
              />
            </button>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* O gatilho media 38px — confortável no mouse, apertado no dedo. Cresce só no
   telefone: no desktop 38px é a medida certa e inflar lá deixaria a tela com
   cara de app de celular esticado. */

/* `fixed` e z acima do backdrop: a lista é filha do body, então não depende
   mais do empilhamento nem do overflow de quem contém o campo. */
.adv-sel-menu {
  @apply fixed z-[9999] w-max max-w-[280px] bg-n-solid-1 border border-n-weak rounded-xl shadow-lg py-1 max-h-72 overflow-y-auto;
}
.adv-sel-item {
  @apply w-full flex items-center gap-2 text-sm px-3 py-2 text-n-slate-12 hover:bg-n-alpha-1 transition-colors;
}
</style>
